import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { trace, type Tracer } from '@opentelemetry/api';
import type { RuntimeConfig } from './config';

declare const __APP_VERSION__: string;

let _initialised = false;

/**
 * propagateTraceHeaderCorsUrls — LITERAL hostname regex per CLAUDE.md.
 *
 * DO NOT collapse to `/^https?:\/\/api\./` — that matches any first-label
 * `api.<anything>` and propagates traceparent into third-party CDNs the user
 * didn't intend. Local dev intentionally crosses 8001/8002/8003 origins so
 * the CORS bug surfaces here, not in hosted smoke.
 */
export const PROPAGATE_TRACE_CORS_URLS: RegExp[] = [
  /^https:\/\/api\.obs\.lab\.performancetest\.ing/,
  /^http:\/\/localhost:(8001|8002|8003)/,
];

export function initTelemetry(config: RuntimeConfig): void {
  if (_initialised) return;
  _initialised = true;

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: 'frontend',
      [ATTR_SERVICE_VERSION]: __APP_VERSION__,
      'deployment.environment': config.deployEnv,
    }),
    spanProcessors: [
      new BatchSpanProcessor(
        new OTLPTraceExporter({
          url: `${config.otelEndpoint.replace(/\/$/, '')}/v1/traces`,
        }),
      ),
    ],
  });
  provider.register({ contextManager: new ZoneContextManager() });

  registerInstrumentations({
    instrumentations: [
      getWebAutoInstrumentations({
        '@opentelemetry/instrumentation-fetch': {
          propagateTraceHeaderCorsUrls: PROPAGATE_TRACE_CORS_URLS,
        },
        // TanStack Query 5 uses fetch by default; XHR coverage is dead weight.
        '@opentelemetry/instrumentation-xml-http-request': { enabled: false },
      }),
    ],
  });
}

export function getTracer(name = 'frontend.ui'): Tracer {
  return trace.getTracer(name);
}
